import { Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { RecommendedAutomationsService } from './recommended-automations.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-recommended-automations-confirm',
    templateUrl: './recommended-automations-confirm.component.html',
    styleUrls: ['./recommended-automations.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RecommendedAutomationsConfirmComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  gridData: any[] = [];
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};

  loading = false;
  showModal = false;

  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to save this automation and automate it?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private service: RecommendedAutomationsService,
    private gtwSnackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.globalService = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.detectChanges();
  }

  @Input('grid-data')
  set _grid_data(v: string) {
    try {
      const data = safeJsonInput<any>(v, null, 'recommended-automations-confirm.parse1');
      if (data && Array.isArray(data) && data.length > 0) {
        this.gridData = data;
        this.tryInit();
      }
    } catch (e) {
      console.log('Grid data input not yet stable.');
    }
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    if (!this.gridData || this.gridData.length === 0) return;
    this.isInitialized = true;
    this.showModal = true;
    this.cdr.detectChanges();
  }

  onConfirm(): void {
    if (this.loading) return;
    if (!this.gridData || this.gridData.length === 0) {
      this.gtwSnackbar.openSnackBar('No grid data to save', 'gtw-snackbar--red');
      return;
    }
    const g = this.globalService?.globalParams || this.globalService || {};
    const sendpopupData: any[] = [];
    for (const item of this.gridData) {
      const d = item.data != null ? item.data : item;
      sendpopupData.push({
        TAX_YEAR: g.tax_year ?? this.filterParams?.tax_year ?? d?.TAX_YEAR,
        SCENARIO: g.scenario ?? this.filterParams?.scenario ?? d?.SCENARIO,
        JCD_KEY: g.jcd_key ?? this.filterParams?.jcd_key ?? d?.JCD_KEY,
        TRIGGER_CHART_KEY: d?.TRIGGER_CHART_KEY,
        TRIGGER_ACCT_KEY: d?.TRIGGER_ACCT_KEY,
        TEMPLATE_ID: d?.SCHDM_TEMPLATE_ID,
        SCHDM_TYPE: d?.SCHDM_TYPE,
        TBBS_ACCT_KEY: d?.TBBS_ACCT_KEY,
        MULTIPLIER: d?.MULTIPLIER,
        USER_DESC: d?.SCHDM_USER_DESC,
        POSTING_TAC_KEY: d?.POSTING_TAC_KEY,
        POSTING_ACCT_KEY: d?.POSTING_ACCT_KEY,
        METHOD_OF_CALC: d?.METHOD_OF_CALC,
        PROVISION_YN: d?.PROVISION_YN
      });
    }

    this.loading = true;
    this.modalConfig = { ...this.modalConfig, loading: true };
    this.cdr.detectChanges();

    this.service.saveRecommAutomation(
      this.GENERAL_CONFIG.base_url,
      sendpopupData,
      this.globalService,
      this.filterParams,
      {}
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        this.loading = false;
        this.modalConfig = { ...this.modalConfig, loading: false };
        this.cdr.detectChanges();
        if (result?.callSuccess !== '1') {
          this.gtwSnackbar.openSnackBar(result?.errorMessage || 'Save failed', 'gtw-snackbar--red');
        } else {
          this.gtwSnackbar.openSnackBar('Data has been saved successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
      },
      error: () => {
        this.loading = false;
        this.modalConfig = { ...this.modalConfig, loading: false };
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  onCancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
